import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";
import LineInputsLoader from "@/Layouts/components/Skeletons/LineInputsLoader";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// MUI
import { Grid, Typography, Stack } from "@mui/material";

// Options
const maxPayoutOptions = [
	{ _id: "frontend", label: "Frontend" },
	{ _id: "backend", label: "Backend" },
];

export default function MaxPayout(props) {
	const {
		sectionId = "maxPayout",
		content = {
			frontend: {
				input: "Percent",
				value: 0,
			},
			backend: {
				input: "Percent",
				value: 0,
			},
			afterChargebacks: false,
		},
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	// Used to add errors to the pay plan context
	const { addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrors = errors?.get("payout");
	const errorsMessages = getErrorsMessages();

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onMaxPayoutUpdate = (updatedMaxPayout) => {
		isValid ? removeSectionError?.(sectionId) : addSectionError?.(sectionId);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(sectionId, null, updatedMaxPayout);
		}
	};

	// States
	const [maxPayout, setMaxPayout] = useCallbackState(
		{
			frontend: {
				input: "Percent",
				value: 0,
			},
			backend: {
				input: "Percent",
				value: 0,
			},
			afterChargebacks: false,
		},
		onMaxPayoutUpdate
	);

	const handleUpdateInput = (area, field, value) => {
		const updated = { ...maxPayout };
		if (field === "input") {
			updated[area][field] = value;
			updated[area]["value"] = 0;
		} else {
			updated[area][field] = value ? Number(value) : 0;
		}
		update(updated);
	};

	const handleSwitch = (value) => {
		const updated = { ...maxPayout };
		updated["afterChargebacks"] = value;
		update(updated);
	};

	const update = (updatedMaxPayout) => {
		setMaxPayout(updatedMaxPayout);
		wasUpdated.current = true;
	};

	useMemo(() => {
		if (maxPayout) {
			validate([
				{
					definition: {
						useKey: "payout",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
									located: "SET",
									distinct: 0,
								},
							],
						},
						errorsMap: ["value"],
						message: "Invalid payout definition",
						validationType: "MULTIPLE",
						specialProperties: {
							atLeastOneValid: true,
						},
					},
					section: [maxPayout.frontend, maxPayout.backend],
				},
			]);
		}
	}, [maxPayout, validate]);

	useEffect(() => {
		setMaxPayout(clone(content));
		return () => {
			setMaxPayout({
				frontend: {
					input: "Percent",
					value: 0,
				},
				backend: {
					input: "Percent",
					value: 0,
				},
				afterChargebacks: false,
			});
			wasUpdated.current = false;
			clearSectionErrors?.(sectionId);
		};
	}, [sectionId, content, setMaxPayout, clearSectionErrors]);

	return (
		<Grid container direction="column" mt={2} sx={{ padding: "0 32px" }}>
			{loading ? (
				<Stack>
					<LineInputsLoader title inputsAmount={2} justifyContent={"flex-start"} mt={0} />
				</Stack>
			) : (
				<Grid container item xs={12}>
					<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} width={1}>
						<Stack direction={"row"} spacing={2}>
							{maxPayoutOptions?.map((option, key) => (
								<Stack key={`max_payout_${sectionId}_${key}`} width={"95px"}>
									<Grid container direction="column" alignItems={"center"}>
										<Grid item xs={2}>
											<Typography className="draw-label" mr={0.2}>
												{option.label}
											</Typography>
										</Grid>
										<Grid item xs={2}>
											<Grid item className="draw-container">
												<InputFormExtension
													data={[
														{
															type: "Dollar",
															max: 999999,
															min: 0,
														},
														{
															type: "Percent",
															max: 100,
															min: 0,
															decimalsEditable: true,
															decimals: 2,
														},
													]}
													defaultType={maxPayout?.[option._id]?.input || "Percent"}
													value={maxPayout?.[option._id]?.value || 0}
													error={Boolean(localErrors?.[key]?.value)}
													onActionUpdate={(value) => handleUpdateInput(option._id, "value", value)}
													onChangeType={(_, input) => handleUpdateInput(option._id, "input", input)}
													readOnly={readOnly}
												/>
											</Grid>
										</Grid>
									</Grid>
								</Stack>
							))}
						</Stack>
						<Stack alignItems={"flex-end"}>
							<Typography className="draw-label" mr={0.2} visibility={"collapse"}>
								Switch
							</Typography>
							<CustomizedSwitches
								disabled={readOnly}
								textLeft
								textChecked="After chargebacks"
								textUnChecked="Before chargebacks"
								checked={Boolean(maxPayout?.afterChargebacks)}
								onChange={handleSwitch}
								tooltip={{
									title: "Implements a cap on total payout before or after chargebacks.",
									placement: "top",
									key: "max-payout-tooltip",
								}}
							/>
						</Stack>
					</Stack>

					{Boolean(errorsMessages?.length) && (
						<Stack mt={1}>
							{errorsMessages.map((message, key) => (
								<TierAlertError key={`error_payout_${sectionId}_${message}_${key}`} text={message} />
							))}
						</Stack>
					)}
				</Grid>
			)}
		</Grid>
	);
}
MaxPayout.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		frontend: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
		backend: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
		afterChargebacks: PropTypes.bool,
	}),
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
